import React, { useState, useEffect, useRef } from 'react'
import { useHospitalSelection } from '../../hooks/useHospitalSelection'
import { usePersistedState } from '../../hooks/usePersistedState'
import LineChart from '../../components/Charts/LineChart'
import DateRangeSlider from '../../components/DateRangeSlider/DateRangeSlider'
import LoadingNotice from '../../components/LoadingNotice/LoadingNotice'
import { generatePDFIds } from '../../utils/pdfIdHelper'
import { parseMonthToValue, formatMonthToSlash } from '../../utils/dataUtils'
import { getSliderIndicesFromValues, initializeSliderValues, TMB_SLIDER_KEY, extractActualMonthValuesFromObjects, checkHasDataForRange } from '../../utils/tmbUtils'
import { isMonthPostLaunch } from '../../utils/tmbLaunchDates'
import { exportToPNG, exportToSVG, exportToCSV, CaptureOptions } from '../../utils/tmbExportUtils'
import { useTmbSlider } from '../../context/TmbSliderContext'
import '../../styles/shared/index.css'
import './HuddleParticipation.css'

const HUDDLE_COLORS = {
    'Yes Huddle': 'rgba(62, 83, 140, 0.6)',
    'No Huddle': 'rgba(213, 149, 82, 0.6)',
    'Not Sure': 'rgba(183, 175, 172, 0.6)'
}

const HUDDLE_TOOLTIP_COLORS = {
    'Yes Huddle': '#3E538C',
    'No Huddle': '#D59552',
    'Not Sure': '#B7AFAC'
}

/** Percent of answered huddle responses (Yes + No + Not Sure). Last value absorbs rounding so the three sum to 100. */
function huddlePercents(yes: number, no: number, notSure: number, decimals: number): [number, number, number] {
    const denom = yes + no + notSure
    if (denom <= 0) return [0, 0, 0]

    const factor = 10 ** decimals
    const roundPct = (count: number) => Math.round((count / denom) * 100 * factor) / factor
    const yesPct = roundPct(yes)
    const noPct = roundPct(no)
    const notSurePct = Number((100 - yesPct - noPct).toFixed(decimals))
    return [yesPct, noPct, notSurePct]
}

interface MonthlyDataRow {
    hospital_id: string
    time_period: string
    yes_count: number
    no_count: number
    not_sure_count: number
    total: number
}

interface ProcessedData {
    categories: string[]
    yesData: (number | null)[]
    noData: (number | null)[]
    notSureData: (number | null)[]
    yesCountData: (number | null)[]
    noCountData: (number | null)[]
    notSureCountData: (number | null)[]
    totalCountData: (number | null)[]
}

const HuddleParticipation: React.FC = () => {
    const { selectedHospitalIds, refreshKey } = useHospitalSelection()
    const { globalMonthList } = useTmbSlider()

    const [rawMonthlyData, setRawMonthlyData] = useState<MonthlyDataRow[]>([])
    const [loading, setLoading] = useState(false)
    const [initialLoading, setInitialLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)

    const [actualMonths, setActualMonths] = useState<Set<number>>(new Set())

    const persistenceKey = `${TMB_SLIDER_KEY}_${Array.isArray(selectedHospitalIds) ? selectedHospitalIds.join('_') : selectedHospitalIds}`
    const [sliderRangeValues, setSliderRangeValues] = usePersistedState<[number, number]>(persistenceKey, [0, 0])

    const [processedData, setProcessedData] = useState<ProcessedData>({
        categories: [],
        yesData: [],
        noData: [],
        notSureData: [],
        yesCountData: [],
        noCountData: [],
        notSureCountData: [],
        totalCountData: []
    })

    const [monthlyMenuOpen, setMonthlyMenuOpen] = useState(false)
    const [chartMenuOpen, setChartMenuOpen] = useState(false)

    const [viewportWidth, setViewportWidth] = useState<number>(() =>
        typeof window !== 'undefined' ? window.innerWidth : 1440
    )
    useEffect(() => {
        const onResize = () => setViewportWidth(window.innerWidth)
        window.addEventListener('resize', onResize, { passive: true } as any)
        return () => window.removeEventListener('resize', onResize as any)
    }, [])

    const chartHeight =
        viewportWidth <= 480 ? 380 :
            viewportWidth <= 768 ? 440 :
                viewportWidth <= 1024 ? 560 :
                    viewportWidth <= 1920 ? 620 :
                        Math.min(Math.round(viewportWidth * 0.32), 900)

    const legendPosition = viewportWidth <= 1024 ? 'bottom' : 'right'

    const legendFontSize = 
        viewportWidth <= 480 ? '11px' : 
            viewportWidth <= 1024 ? '12px' : 
                viewportWidth <= 1920 ? '12px' :
                    `${Math.min(Math.round(viewportWidth * 0.0065), 16)}px`
    
    const legendMarkerSize = 
        viewportWidth <= 480 ? '9px' : 
            viewportWidth <= 1920 ? '10px' :
                `${Math.min(Math.round(viewportWidth * 0.006), 14)}px`
    
    const legendGap = viewportWidth <= 480 ? '5px' : '6px'
    
    const gridPadding =
        viewportWidth <= 768
            ? { top: 10, right: 10, bottom: 30, left: 10 }
            : viewportWidth <= 1920 
                ? { top: 20, right: legendPosition === 'right' ? 100 : 20, bottom: 40, left: 15 }
                : { top: 25, right: legendPosition === 'right' ? 150 : 30, bottom: 50, left: 20 }

    const monthlyTableRef = useRef<HTMLDivElement>(null)
    const chartContainerRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        if (!globalMonthList.length) return
        setSliderRangeValues(prev => initializeSliderValues(globalMonthList, prev, 12))
    }, [globalMonthList, setSliderRangeValues])

    useEffect(() => {
        if (!selectedHospitalIds || selectedHospitalIds.length === 0) {
            setRawMonthlyData([])
            setProcessedData({ categories: [], yesData: [], noData: [], notSureData: [], yesCountData: [], noCountData: [], notSureCountData: [], totalCountData: [] })
            setLoading(false)
            setInitialLoading(false)
            setError(null)
            return
        }

        const hospitalIdsParam = Array.isArray(selectedHospitalIds)
            ? selectedHospitalIds.join(',')
            : selectedHospitalIds

        setLoading(true)
        setError(null)

        const monthlyURL = `/api/tmb/huddle_participation_monthly/${hospitalIdsParam}`

        fetch(monthlyURL)
            .then(r => r.ok ? r.json() : Promise.reject(`Monthly API error: ${r.status}`))
            .then((monthlyData) => {
                const monthlyRows = monthlyData?.response?.result?.rows || []

                if (monthlyRows.length === 0) {
                    setError('No data available for selected hospital(s)')
                    setLoading(false)
                    setInitialLoading(false)
                    return
                }

                const monthlyCols: string[] = (monthlyData?.response?.result?.column_order || []).map((c: any) => String(c).trim().toLowerCase())
                const col = (name: string, fallback: number) => {
                    const idx = monthlyCols.indexOf(name)
                    return idx !== -1 ? idx : fallback
                }
                // Zoho column order:
                // hospital_id, month, phase, yes_count, no_count, not_sure_count,
                // not_reported_count, total, yes_percent, no_percent, not_sure_percent,
                // not_reported_percent, survey_date
                const hospitalIdIdx = col('hospital_id', 0)
                const monthIdx = col('month', 1)
                const yesCountIdx = col('yes_count', 3)
                const noCountIdx = col('no_count', 4)
                const notSureCountIdx = col('not_sure_count', 5)
                const totalIdx = col('total', 7)

                const parsed: MonthlyDataRow[] = monthlyRows.map((row: any[]) => ({
                    hospital_id: row[hospitalIdIdx],
                    time_period: row[monthIdx],
                    yes_count: parseFloat(row[yesCountIdx]) || 0,
                    no_count: parseFloat(row[noCountIdx]) || 0,
                    not_sure_count: parseFloat(row[notSureCountIdx]) || 0,
                    total: parseFloat(row[totalIdx]) || 0,
                }))

                const launchFiltered = parsed.filter(row => isMonthPostLaunch(row.hospital_id, row.time_period))

                setRawMonthlyData(launchFiltered)

                setActualMonths(extractActualMonthValuesFromObjects(launchFiltered, (row) => row.time_period))

                setLoading(false)
                setInitialLoading(false)
            })
            .catch(err => {
                setError(err.message || 'Failed to load data')
                setLoading(false)
                setInitialLoading(false)
            })
    }, [selectedHospitalIds, refreshKey])

    useEffect(() => {
        if (!globalMonthList.length) {
            setProcessedData({ categories: [], yesData: [], noData: [], notSureData: [], yesCountData: [], noCountData: [], notSureCountData: [], totalCountData: [] })
            return
        }

        const maxIndex = globalMonthList.length - 1
        const defaultStartIdx = Math.max(0, maxIndex - 11)

        let minMonthVal = sliderRangeValues[0]
        let maxMonthVal = sliderRangeValues[1]

        if (!minMonthVal || !maxMonthVal) {
            minMonthVal = globalMonthList[defaultStartIdx]?.value || 0
            maxMonthVal = globalMonthList[maxIndex]?.value || 0
        }

        if (minMonthVal === 0 && maxMonthVal === 0) return

        const rangeMonths = globalMonthList.filter(m => m.value >= minMonthVal && m.value <= maxMonthVal)

        if (rangeMonths.length === 0) {
            setProcessedData({ categories: [], yesData: [], noData: [], notSureData: [], yesCountData: [], noCountData: [], notSureCountData: [], totalCountData: [] })
            return
        }

        interface MonthAggregation {
            yesCount: number
            noCount: number
            notSureCount: number
            total: number
        }
        const monthDataMap: Record<string, MonthAggregation> = {}

        rawMonthlyData.forEach(row => {
            const val = parseMonthToValue(row.time_period)
            if (!val || val < minMonthVal || val > maxMonthVal) return

            const monthKey = formatMonthToSlash(row.time_period)
            if (!monthDataMap[monthKey]) {
                monthDataMap[monthKey] = {
                    yesCount: 0, noCount: 0, notSureCount: 0, total: 0
                }
            }
            const data = monthDataMap[monthKey]
            data.yesCount += row.yes_count
            data.noCount += row.no_count
            data.notSureCount += row.not_sure_count
            data.total += row.total
        })

        const categories: string[] = []
        const yesData: (number | null)[] = []
        const noData: (number | null)[] = []
        const notSureData: (number | null)[] = []
        const yesCountData: (number | null)[] = []
        const noCountData: (number | null)[] = []
        const notSureCountData: (number | null)[] = []
        const totalCountData: (number | null)[] = []

        rangeMonths.forEach(m => {
            const monthKey = `${String(m.value % 100).padStart(2, '0')}/${Math.floor(m.value / 100)}`
            const data = monthDataMap[monthKey]

            categories.push(monthKey)

            if (!data) {
                yesData.push(null)
                noData.push(null)
                notSureData.push(null)
                yesCountData.push(null)
                noCountData.push(null)
                notSureCountData.push(null)
                totalCountData.push(null)
                return
            }

            const [yesPercent, noPercent, notSurePercent] = huddlePercents(
                data.yesCount, data.noCount, data.notSureCount, 1
            )

            yesData.push(Math.round(yesPercent))
            noData.push(Math.round(noPercent))
            notSureData.push(Math.round(notSurePercent))
            yesCountData.push(Math.round(data.yesCount))
            noCountData.push(Math.round(data.noCount))
            notSureCountData.push(Math.round(data.notSureCount))
            totalCountData.push(Math.round(data.total))
        })

        setProcessedData({ categories, yesData, noData, notSureData, yesCountData, noCountData, notSureCountData, totalCountData })
    }, [rawMonthlyData, globalMonthList, sliderRangeValues])

    const handleSliderChange = (startIndex: number, endIndex: number) => {
        const startVal = globalMonthList[startIndex]?.value || 0
        const endVal = globalMonthList[endIndex]?.value || 0

        if (!startVal || !endVal) return
        setSliderRangeValues([startVal, endVal])
    }

    const fullWidthCaptureOptions: CaptureOptions = {
        fullWidth: true,
        scrollContainerSelector: '.monthly-table-scroll',
        fullWidthElementSelector: '.monthly-table',
        cssContextClass: 'huddle-participation-page'
    }

    const standardCaptureOptions: CaptureOptions = {
        cssContextClass: 'huddle-participation-page'
    }

    const handleDownloadPNG = async (ref: React.RefObject<HTMLDivElement>, filename: string, title?: string, options?: CaptureOptions) => {
        if (!ref.current) return
        const titleConfig = title ? { title } : undefined
        await exportToPNG(ref.current, filename, titleConfig, options || standardCaptureOptions)
    }

    const handleDownloadSVG = async (ref: React.RefObject<HTMLDivElement>, title: string | undefined, filename: string, options?: CaptureOptions) => {
        if (!ref.current) return
        const titleConfig = title ? { title } : undefined
        await exportToSVG(ref.current, filename, titleConfig, options || standardCaptureOptions)
    }

    const handleDownloadCSV = (headers: string[], rows: (string | number)[][], filename: string) => {
        exportToCSV({ headers, rows, filename })
    }

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (!(e.target as Element).closest('.export-menu')) {
                setMonthlyMenuOpen(false)
                setChartMenuOpen(false)
            }
        }
        document.addEventListener('click', handleClickOutside)
        return () => document.removeEventListener('click', handleClickOutside)
    }, [])

    const HamburgerIcon = () => (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
            <g>
                <rect x="4" y="6" width="16" height="2" rx="1" />
                <rect x="4" y="11" width="16" height="2" rx="1" />
                <rect x="4" y="16" width="16" height="2" rx="1" />
            </g>
        </svg>
    )

    if (initialLoading) {
        return <LoadingNotice title="Loading huddle participation data..." />
    }

    if (error) {
        return (
            <div className="huddle-participation-page tmb-page">
                <div className="no-data-state">
                    <p>Error: {error}</p>
                </div>
            </div>
        )
    }

    const { categories, yesData, noData, notSureData, yesCountData, noCountData, notSureCountData, totalCountData } = processedData

    const chartSeries = [
        { name: 'Yes Huddle', data: yesData },
        { name: 'No Huddle', data: noData },
        { name: 'Not Sure', data: notSureData }
    ]

    const renderHuddleTooltip = ({ category, dataPointIndex }: { category: string; dataPointIndex: number }): string => {
        const yesCount = yesCountData[dataPointIndex] ?? 0
        const noCount = noCountData[dataPointIndex] ?? 0
        const notSureCount = notSureCountData[dataPointIndex] ?? 0
        const answered = yesCount + noCount + notSureCount
        const [yesPct, noPct, notSurePct] = huddlePercents(yesCount, noCount, notSureCount, 1)
        const rows = [
            { name: 'Yes Huddle', count: yesCount, percent: yesPct },
            { name: 'No Huddle', count: noCount, percent: noPct },
            { name: 'Not Sure', count: notSureCount, percent: notSurePct },
        ]

        const rowHtml = rows.map((row) => {
            const color = HUDDLE_TOOLTIP_COLORS[row.name as keyof typeof HUDDLE_TOOLTIP_COLORS]
            const pct = `${row.percent.toFixed(1)}%`
            return `
                <div style="padding:6px 10px; background:#fff; display:flex; align-items:center;">
                    <span style="background:${color}; width:10px; height:10px; border-radius:50%; margin-right:8px; display:inline-block;"></span>
                    <span style="font-size:12px; color:#000; margin-right:5px;">${row.name}: </span>
                    <span style="font-size:12px; font-weight:600; color:#000;">${row.count} (${pct})</span>
                </div>
            `
        }).join('')

        return `
            <div style="font-family:Poppins, Segoe UI, sans-serif; font-size:12px; background:#fff;">
                <div style="background:#ECEFF1; font-size:12px; border-bottom:1px solid #ddd; padding:8px 10px; color:#000;">
                    ${category} · N = ${answered}
                </div>
                ${rowHtml}
            </div>
        `
    }

    const naOr = (v: number | null): number => v ?? 0

    const monthlyCSVHeaders = ['', ...categories]
    const monthlyCSVRows = [
        ['Yes Huddle', ...yesCountData.map(naOr)],
        ['No Huddle', ...noCountData.map(naOr)],
        ['Not Sure', ...notSureCountData.map(naOr)],
        ['Not Reported', ...totalCountData.map((t, i) => {
            if (t === null) return 0
            const reported = (yesCountData[i] ?? 0) + (noCountData[i] ?? 0) + (notSureCountData[i] ?? 0)
            return Math.max(0, t - reported)
        })],
        ['Grand Total', ...totalCountData.map(naOr)]
    ]

    const [sliderStartIndex, sliderEndIndex] = getSliderIndicesFromValues(globalMonthList, sliderRangeValues)
    const hasDataForRange = checkHasDataForRange(sliderRangeValues[0], sliderRangeValues[1], actualMonths)

    return (
        <div id={generatePDFIds.dashboard()} className="huddle-participation-page tmb-page">
            <div className="content-wrapper">
                <div className="header-wrapper">
                    <span className="page-title">Huddle Participation by Month</span>
                    {globalMonthList.length > 0 && (
                        <DateRangeSlider
                            monthList={globalMonthList}
                            startIndex={sliderStartIndex}
                            endIndex={sliderEndIndex}
                            onChange={handleSliderChange}
                        />
                    )}
                </div>

                <div className="section-card">
                    {!categories.length || !hasDataForRange ? (
                        <div style={{ padding: '14px 12px', fontSize: '13px', fontWeight: 600, color: '#000000' }}>
                            No data available
                        </div>
                    ) : (<>
                    <div className="export-menu chart-export-menu">
                        <button
                            className="hamburger-btn"
                            onClick={(e) => { e.stopPropagation(); setChartMenuOpen(!chartMenuOpen) }}
                        >
                            <HamburgerIcon />
                        </button>
                        <div className={`export-dropdown ${chartMenuOpen ? 'show' : ''}`}>
                            <div className="menu-item" onClick={async () => {
                                const hospitalIdsStr = Array.isArray(selectedHospitalIds) ? selectedHospitalIds.join('_') : selectedHospitalIds
                                await handleDownloadPNG(chartContainerRef, `Huddle_Participation_by_Month_${hospitalIdsStr}`, 'Huddle Participation by Month')
                                setChartMenuOpen(false)
                            }}>Download PNG</div>
                            <div className="menu-item" onClick={async () => {
                                const hospitalIdsStr = Array.isArray(selectedHospitalIds) ? selectedHospitalIds.join('_') : selectedHospitalIds
                                await handleDownloadSVG(chartContainerRef, 'Huddle Participation by Month', `Huddle_Participation_by_Month_${hospitalIdsStr}`)
                                setChartMenuOpen(false)
                            }}>Download SVG</div>
                            <div className="menu-item" onClick={() => {
                                const hospitalIdsStr = Array.isArray(selectedHospitalIds) ? selectedHospitalIds.join(',') : selectedHospitalIds
                                const chartCSVHeaders = [
                                    'Hospital ID(s)',
                                    'Month',
                                    'N',
                                    'Yes Huddle',
                                    'Yes Huddle (%)',
                                    'No Huddle',
                                    'No Huddle (%)',
                                    'Not Sure',
                                    'Not Sure (%)',
                                ]
                                const chartCSVRows = categories.map((cat, i) => {
                                    const yesCount = yesCountData[i] ?? 0
                                    const noCount = noCountData[i] ?? 0
                                    const notSureCount = notSureCountData[i] ?? 0
                                    const answered = yesCount + noCount + notSureCount
                                    const [yesPct, noPct, notSurePct] = huddlePercents(yesCount, noCount, notSureCount, 1)
                                    return [
                                        hospitalIdsStr,
                                        cat,
                                        answered,
                                        yesCount,
                                        answered > 0 ? yesPct.toFixed(1) : '-',
                                        noCount,
                                        answered > 0 ? noPct.toFixed(1) : '-',
                                        notSureCount,
                                        answered > 0 ? notSurePct.toFixed(1) : '-',
                                    ]
                                })
                                handleDownloadCSV(chartCSVHeaders, chartCSVRows, `Huddle_Participation_by_Month_${hospitalIdsStr.replace(/,/g, '_')}`)
                                setChartMenuOpen(false)
                            }}>Download CSV</div>
                        </div>
                    </div>
                    <div id={generatePDFIds.chart(1)} className="chart-container" ref={chartContainerRef}>
                            <LineChart
                            categories={categories}
                            series={chartSeries}
                            height={chartHeight}
                            yAxisLabel="Percentage (%)"
                            xAxisLabel="Month"
                            xAxisLabelRotate={-45}
                            valueSuffix="%"
                            colors={[HUDDLE_COLORS['Yes Huddle'], HUDDLE_COLORS['No Huddle'], HUDDLE_COLORS['Not Sure']]}
                            min={0}
                            max={100}
                            tickAmount={10}
                            isLoading={loading}
                            fontFamily="Poppins, Segoe UI, sans-serif"
                            xAxisLabelFontSize="11px"
                            yAxisLabelFontSize="13px"
                            xAxisTitleFontSize="13px"
                            xAxisTitleFontWeight={500}
                            yAxisTitleFontSize="13px"
                            yAxisTitleFontWeight={500}
                            dataLabelsFontWeight={400}
                            showToolbar={true}
                            showLegend={true}
                            legendPosition={legendPosition as any}
                            legendAlign={'center'}
                            decimals={0}
                            showDataLabels={true}
                            dataLabelsOffsetY={-8}
                            legendItemMargin={{ horizontal: 0, vertical: 0 }}
                            legendOffsetY={0}
                            showHorizontalGridLines={false}
                            gridPadding={gridPadding}
                            useCustomLegend={true}
                            legendGap={legendGap}
                            legendFontSize={legendFontSize}
                            legendFontWeight={400}
                            legendMarkerSize={legendMarkerSize}
                            customTooltip={renderHuddleTooltip}
                        />
                    </div>

                    <div className="tables-container">
                        <div className="table-wrapper" ref={monthlyTableRef}>
                            <div className="table-header">
                                <div className="table-title">Monthly Data</div>
                                <div className="export-menu">
                                    <button
                                        className="hamburger-btn"
                                        onClick={(e) => { e.stopPropagation(); setMonthlyMenuOpen(!monthlyMenuOpen) }}
                                    >
                                        <HamburgerIcon />
                                    </button>
                                    <div className={`export-dropdown ${monthlyMenuOpen ? 'show' : ''}`}>
                                        <div className="menu-item" onClick={async () => { await handleDownloadPNG(monthlyTableRef, 'Huddle_Monthly_Data', undefined, fullWidthCaptureOptions); setMonthlyMenuOpen(false); }}>Download PNG</div>
                                        <div className="menu-item" onClick={async () => { await handleDownloadSVG(monthlyTableRef, undefined, 'Huddle_Monthly_Data', fullWidthCaptureOptions); setMonthlyMenuOpen(false); }}>Download SVG</div>
                                        <div className="menu-item" onClick={() => { handleDownloadCSV(monthlyCSVHeaders, monthlyCSVRows, 'Huddle_Monthly_Data'); setMonthlyMenuOpen(false) }}>Download CSV</div>
                                    </div>
                                </div>
                            </div>
                            <div className="monthly-table-scroll">
                                <table className="data-table monthly-table">
                                    <thead>
                                        <tr>
                                            <th></th>
                                            {categories.map(month => (
                                                <th key={month}>{month}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td>Yes Huddle</td>
                                            {yesCountData.map((val, i) => (
                                                <td key={i}>{val ?? 0}</td>
                                            ))}
                                        </tr>
                                        <tr>
                                            <td>No Huddle</td>
                                            {noCountData.map((val, i) => (
                                                <td key={i}>{val ?? 0}</td>
                                            ))}
                                        </tr>
                                        <tr>
                                            <td>Not Sure</td>
                                            {notSureCountData.map((val, i) => (
                                                <td key={i}>{val ?? 0}</td>
                                            ))}
                                        </tr>
                                        <tr>
                                            <td>Not Reported</td>
                                            {totalCountData.map((total, i) => {
                                                if (total === null) return <td key={i}>0</td>
                                                const reported = (yesCountData[i] ?? 0) + (noCountData[i] ?? 0) + (notSureCountData[i] ?? 0)
                                                return <td key={i}>{Math.max(0, total - reported)}</td>
                                            })}
                                        </tr>
                                        <tr className="grand-total-row">
                                            <td>Grand Total</td>
                                            {totalCountData.map((total, i) => (
                                                <td key={i}>{total ?? 0}</td>
                                            ))}
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                    </>)}
                </div>
            </div>
        </div>
    )
}

export default HuddleParticipation
